import PropTypes from 'prop-types'
import classNames from 'classnames'
import { noop } from 'utils'
import {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import useFocusOnError from 'utils/hooks/useFocusOnError'
import { useUpdatingRef } from 'utils/hooks/useUpdatingRef'

let idIndex = 0

const RadioButtonGroupContext = createContext({
  name: '',
  focusOnError: false,
  error: null,
  setValue: noop,
})

const RadioButton = forwardRef(
  (
    {
      id,
      defaultChecked,
      checked,
      checkValue,
      value,
      name = '',
      label = '',
      disabled = false,
      onChange = noop,
      onValueChange = noop,
      onClick = noop,
      tiaraAction,
    },
    outerRef
  ) => {
    const [uniqIndex] = useState(idIndex++)
    const {
      name: groupAssignedName,
      focusOnError,
      error,
      setValue,
    } = useContext(RadioButtonGroupContext)
    const innerRef = useRef()
    const ref = outerRef || innerRef

    useFocusOnError(ref, error, focusOnError)

    const appliedChecked =
      checkValue === undefined ? checked : checkValue === value
    return (
      <span
        className={classNames({
          box_radioinp: label !== '',
          radio_inp: label === '',
          in_active: disabled,
          on: appliedChecked,
        })}>
        <input
          ref={ref}
          type="radio"
          id={id || `inpRadio_$$${uniqIndex}`}
          className="inp_radio"
          name={groupAssignedName || name}
          value={typeof value === 'string' ? value : 'on'}
          defaultChecked={defaultChecked}
          checked={
            appliedChecked === undefined ? appliedChecked : !!appliedChecked
          }
          onClick={onClick}
          disabled={disabled}
          onChange={e => {
            setValue(value)
            onValueChange(value)
            onChange(e)
          }}
        />
        <label
          htmlFor={id || `inpRadio_$$${uniqIndex}`}
          className="lab_radio"
          data-tiara-action-name={tiaraAction}>
          <span className="img_comm ico_radio" />
          {label}
        </label>
      </span>
    )
  }
)

RadioButton.displayName = 'RadioButton'
RadioButton.propTypes = {
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
  onClick: PropTypes.func,
  value: PropTypes.any,
  checkValue: PropTypes.any,
  tiaraAction: PropTypes.string,
}

const RadioButtonGroup = ({
  id,
  className,
  name,
  children,
  inline = false,
  error = null,
  focusOnError = false,
  onValueChange = noop,
  onChange = noop,
}) => {
  const onValueChangeRef = useUpdatingRef(onValueChange)
  const [uniqIndex] = useState(idIndex++)
  const [value, setValue] = useState()
  const Tag = inline ? 'span' : 'div'

  useEffect(() => {
    if (value === undefined) return
    onValueChangeRef.current(value)
  }, [onValueChangeRef, value])

  return (
    <RadioButtonGroupContext.Provider
      value={{
        name: name || `radioButtonGroup_$$${uniqIndex}`,
        focusOnError,
        error,
        setValue,
      }}>
      <>
        <Tag id={id} className={className} onChange={onChange}>
          {children}
        </Tag>
      </>
    </RadioButtonGroupContext.Provider>
  )
}

RadioButtonGroup.displayName = 'RadioButton.Group'

RadioButtonGroup.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  inline: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onValueChange: PropTypes.func,
  error: PropTypes.any,
  focusOnError: PropTypes.bool,
}

RadioButton.Group = RadioButtonGroup

export default RadioButton
